import { observer } from 'mobx-react-lite'
import { computed } from 'mobx'
import { useRootStore } from 'stores/RootStore'
import { ViewTypes } from 'stores/NotificationsStore'
import { IconButton } from 'components/IconButton/IconButton'
import { ReactComponent as BackIcon } from 'assets/icons/arrow--left_16.svg'
import Styles from './NotificationsTray.module.scss'
import cx from 'classnames'
import { ScrollContainer } from 'components/DocRightRailV2/ScrollContainer/ScrollContainer'
import { NotificationItem } from './NotificationItem/NotificationItem'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { LoadingState } from './LoadingState/LoadingState'
import dayjs from 'dayjs'
import { useNotificationsQuery } from './hooks/useNotificationsQuery'
import { Heading } from 'blaze-ui/Heading/Heading'

interface Props {
  onClose: () => void
}

export const ReadNotifications = observer(({ onClose }: Props) => {
  const { notifications } = useRootStore()
  const { isLoading, hasNextPage, fetchNextPage, isRefetching, isFetchingNextPage } = useNotificationsQuery({
    read: true,
  })
  const showInfiniteScroll = !isLoading && hasNextPage !== undefined

  const sevenDaysAgo = dayjs().subtract(7, 'days')
  const fourteenDaysAgo = dayjs().subtract(14, 'days')
  const sections = [
    {
      title: 'Last 7 days',
      items: computed(() => notifications.read.filter((n) => dayjs(n.updated_at) > sevenDaysAgo)).get(),
    },
    {
      title: 'Last 14 days',
      items: computed(() =>
        notifications.read.filter((n) => dayjs(n.updated_at) < sevenDaysAgo && dayjs(n.updated_at) > fourteenDaysAgo),
      ).get(),
    },
    {
      title: 'Earlier',
      items: computed(() => notifications.read.filter((n) => dayjs(n.updated_at) < fourteenDaysAgo)).get(),
    },
  ]

  return (
    <>
      <div className={cx(Styles.header, Styles.readHeader)}>
        <div className={Styles.titleContainer}>
          <IconButton
            icon={BackIcon}
            size="md"
            className={Styles.backButton}
            onPress={() => notifications.setCurrentView(ViewTypes.Unread)}
          />
          <Heading level={4}>Archived Notifications</Heading>
        </div>
      </div>
      <ScrollContainer className={Styles.scrollContainer}>
        {isLoading || (isRefetching && !isFetchingNextPage) ? (
          <LoadingState />
        ) : (
          <>
            {sections
              .filter(({ items }) => items.length > 0)
              .map((section) => (
                <>
                  <div className={Styles.section}>{section.title}</div>
                  {section.items.map((notification) => (
                    <NotificationItem notification={notification} key={notification.id} onClose={onClose} />
                  ))}
                </>
              ))}
            {showInfiniteScroll && (
              <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage} className={Styles.infiniteScroll}>
                <LoadingState />
              </InfiniteScroll>
            )}
          </>
        )}
      </ScrollContainer>
    </>
  )
})
